import React, { useCallback, useContext, useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { CreateOrUpdateInvoiceHandler } from '../types';
import { ButtonBarButtonsProp } from '../../ButtonBar';
import { Icon } from '../../../blocks/icons';
import { InvoiceIsSendable } from '../helpers';
import {
	useInvoiceEditButtonBarSendButton_SendOrResendInvoiceMutation,
	BundleStatusEnum,
	InvoiceStatus,
	UseInvoiceEditButtonBarSendButtonInvoiceFragment,
} from '#types';
import { useToast } from '../../../hooks/useToast';
import { ContactsContext } from '../../../context/ContactsContextProvider/ContactsContextProvider';
import rpcShared from '@rockpapercoin/rpc-shared';
import { displayStripeNeedsModal } from '../../../redux/actions';
import { useDispatch } from 'react-redux';
import MuiPayToModal from '../../../mui/MuiModal/MuiPayToModal/MuiPayToModal';
import { useTrackInvoiceSent } from '../../../lib/helpers/invoice';
import useContactIsChanging from '../../../hooks/useContactIsChanging';
import { ButtonGroupButtonProps } from '../../ButtonGroupButton';
import {
	CustomizeCustomerNotificationModal,
	CustomizedEmail,
	MessagingType,
} from '../../CustomizeCustomerNotificationModal';
import { useDocumentEditFollowupActions } from '../../../lib/helpers/bundle/useDocumentEditFollowupActions';
import { DocumentTypeEnum } from '../../SuggestDocumentCreateModal';
import { createBundleObject } from '../../../lib/helpers';
import { useHeadsUpModal } from '../../../hooks/useHeadsUpModal';
import { INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID } from '../../../lib/editorBlockerIds';
import { useBlockingRouter } from '../../../hooks/useBlockingRouter';
import { useSessionStorage } from '../../../hooks/useSessionStorage';
import { type useInvoiceEdit } from '../useInvoiceEdit';

export const INTENDED_TO_SEND_KEY = 'intendedToSend';

const SendIntent = {
	Send: 'Send',
	CustomizeAndSend: 'CustomizeAndSend',
} as const;

type IntendedSendRetryPayload = {
	intent: keyof typeof SendIntent;
};

export const useInvoiceEditButtonBarSendButtonInvoiceFragment = gql`
	fragment useInvoiceEditButtonBarSendButtonInvoice on Invoice {
		id
		status
		automatedRemindersEnabled
		eventDate
		dueDate
		title
		totalAmount
		contact {
			id
		}
		lineItems {
			id
			rate
			quantity
			taxPercentage
		}
		discountAmount
		paymentInstallments {
			id
			status
			dueDate
			amountPaid
			amountDue
			gratuityPaid
			paymentDate
			convenienceFee
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InvoiceEditButtonBarSendButton_sendOrResendInvoice = gql`
	mutation InvoiceEditButtonBarSendButton_sendOrResendInvoice(
		$data: SendOrResendInvoiceInput
		$where: InvoiceWhereUniqueInput!
	) {
		sendOrResendInvoice(data: $data, where: $where) {
			id
			sentAt
			...useInvoiceEditButtonBarSendButtonInvoice
		}
	}
	${ useInvoiceEditButtonBarSendButtonInvoiceFragment }
`;

export type useInvoiceEditButtonBarSendButtonProps = {
	invoice?: Omit<UseInvoiceEditButtonBarSendButtonInvoiceFragment, 'id'> & {
		id?: string;
	};
	onCreateOrUpdateHandler: CreateOrUpdateInvoiceHandler;
	disabled?: boolean;
	bundle?: NonNullable<ReturnType<typeof createBundleObject>>;
	setContactOptional: React.Dispatch<React.SetStateAction<boolean>>;
	openCustomizeEmailModal: boolean;
	setOpenCustomizeEmailModal: React.Dispatch<React.SetStateAction<boolean>>;
	setSkipInstallmentCalculation: React.Dispatch<React.SetStateAction<boolean>>;
	validate: ReturnType<typeof useInvoiceEdit>['validate'];
	initialStateReady: boolean;
};

type useInvoiceEditButtonBarSendButtonReturnType = {
	button: ButtonBarButtonsProp['buttons'];
	jsx: React.JSX.Element;
};

export const useInvoiceEditButtonBarSendButton = ( {
	invoice,
	onCreateOrUpdateHandler,
	disabled,
	bundle,
	setContactOptional,
	openCustomizeEmailModal,
	setOpenCustomizeEmailModal,
	setSkipInstallmentCalculation,
	validate,
	initialStateReady,
}: useInvoiceEditButtonBarSendButtonProps ): useInvoiceEditButtonBarSendButtonReturnType => {
	const router = useBlockingRouter();
	const {
		setSessionStorageItem,
		getSessionStorageItem,
		removeSessionStorageItem,
	} = useSessionStorage();
	const { showError, showSuccess } = useToast();
	// we'll want to be able to send some segment data
	const trackSegmentInvoiceSent = useTrackInvoiceSent(
		!!invoice?.status && invoice?.status !== InvoiceStatus.Draft
	);
	// and we'll need the current client-side contact configuration for bundle usage
	const contactsContext = useContext( ContactsContext );
	// invoiceEditContext won't have the contact when this is clicked if the contact has changed since the last save
	const contactIdForSentInvoiceDiffersFromCurrent = useContactIsChanging(
		invoice?.contact?.id
	);
	const shouldRenderSendButton = InvoiceIsSendable( invoice?.status );
	const dispatch = useDispatch();
	const [ sendOrResendInvoice ] =
		useInvoiceEditButtonBarSendButton_SendOrResendInvoiceMutation();
	const [ loading, setLoading ] = useState( false );
	const [ showPayToModal, setShowPayToModal ] = useState( false );

	const isSavingDraft =
		!invoice?.status || invoice.status === InvoiceStatus.Draft;

	const changingContact =
		( contactIdForSentInvoiceDiffersFromCurrent &&
			invoice &&
			invoice?.status !== InvoiceStatus.Draft ) ||
		false;

	const {
		onSend: onSendHandleAlertsAndBundleNavigation,
		isLastDocumentInBundle,
	} = useDocumentEditFollowupActions( {
		documentType: DocumentTypeEnum.Invoice,
		isResending: invoice ? invoice?.status !== InvoiceStatus.Draft : false,
		bundle,
	} );

	const buttonText =
		bundle && !isLastDocumentInBundle
			? 'Next'
			: changingContact
				? 'Save & Send'
				: isSavingDraft
					? 'Send'
					: 'Resend';

	const validateAndCreateOrUpdate = useCallback(
		async (
			intendToSend: NonNullable<
				Parameters<typeof onCreateOrUpdateHandler>[0]
			>['intendToSend']
		) => await onCreateOrUpdateHandler( { intendToSend } ),
		[ onCreateOrUpdateHandler ]
	);

	const markIntentToSendForValidationRetry = useCallback(
		( payload?: IntendedSendRetryPayload ) => {
			setSessionStorageItem(
				INTENDED_TO_SEND_KEY,
				JSON.stringify( payload ?? {} )
			);
		},
		[ setSessionStorageItem ]
	);

	const validateAndSavePriorToSending = useCallback(
		async () => await validateAndCreateOrUpdate( false ),
		[ validateAndCreateOrUpdate ]
	);

	const performSend = useCallback(
		async (
			customizedEmail?: CustomizedEmail,
			skipValidateAndSave?: boolean
		) => {
			setContactOptional( ( prevState ) => ( prevState ? false : prevState ) );
			await new Promise( ( resolve ) => setTimeout( resolve, 0 ) ); // wait a beat for a render cycle for state to update
			setLoading( true );
			const invoiceId = invoice?.id;
			if ( !invoiceId ) {
				const success = await validateAndSavePriorToSending();
				if ( success ) {
					markIntentToSendForValidationRetry( {
						intent: SendIntent.Send,
					} );
					await router.push( `/invoice/${ success.id }?tab=edit`, {
						overrideBlockers: [ INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID ],
					} );
					setLoading( false );
					return true;
				}
				setLoading( false );
				return false;
			}
			const success = skipValidateAndSave
				? validate( true )
				: await validateAndSavePriorToSending();
			if ( success ) {
				if ( bundle ) {
					delete bundle.dueDate;
					delete bundle.title;
				}
				setSkipInstallmentCalculation( true );
				const response = await sendOrResendInvoice( {
					variables: {
						where: { id: invoiceId },
						data: {
							...( bundle
								? {
									bundle: {
										contactID: bundle.contactID,
										contractBundleStatus: bundle.contractBundleStatus,
										proposalBundleStatus: bundle.proposalBundleStatus,
										eventDate: bundle.eventDate,
										invoiceBundleStatus: BundleStatusEnum.Sent,
									},
								  }
								: {} ),
							customizedEmail,
						},
					},
				} );
				if ( response.errors ) {
					validate( true );
					setSkipInstallmentCalculation( false );
					// Show that their changes are saved even if there were errors sending
					if ( isSavingDraft ) {
						showSuccess( 'Invoice draft saved' );
					} else {
						showSuccess( 'Invoice successfully updated' );
					}
					// Now handle the errors
					const filteredErrors = rpcShared.errorFiltering.filterApiErrors(
						response,
						[ 'hasStripeNeeds', 'hasStripeConnectNeeds' ],
						{
							hasStripeNeeds: () => {
								dispatch(
									displayStripeNeedsModal( {
										active: true,
										props: { purpose: 'sendInvoice' },
									} )
								);
							},
							hasStripeConnectNeeds: () => {
								setShowPayToModal( true );
							},
						}
					);
					// display any remaining errors as PUNs
					filteredErrors.errors?.forEach( ( error ) => showError( error ) );
				} else if ( response.data?.sendOrResendInvoice ) {
					await trackSegmentInvoiceSent( response.data?.sendOrResendInvoice );
					setSkipInstallmentCalculation( true ); // also skip installment calculation for navigation change
					if ( response.data?.sendOrResendInvoice.contact?.id ) {
						await onSendHandleAlertsAndBundleNavigation( {
							eventDate: response.data?.sendOrResendInvoice.eventDate,
							title: response.data?.sendOrResendInvoice.title,
							documentId: response.data?.sendOrResendInvoice.id,
							contactID: response.data.sendOrResendInvoice.contact.id,
						} );
					}
					setLoading( false );
					return true;
				}
			}
			setLoading( false );
			return false;
		},
		[
			dispatch,
			validateAndSavePriorToSending,
			isSavingDraft,
			sendOrResendInvoice,
			trackSegmentInvoiceSent,
			showError,
			showSuccess,
			onSendHandleAlertsAndBundleNavigation,
			bundle,
			invoice?.id,
			markIntentToSendForValidationRetry,
			setContactOptional,
			setSkipInstallmentCalculation,
			router,
			validate,
		]
	);

	/* If the vendor tried to send an invoice that had validation errors
	preventing sending, but allowing saving, now that we have an ID and
	have navigated to the new invoice page, trigger validation again */
	const intendedToSend = getSessionStorageItem( INTENDED_TO_SEND_KEY );
	useEffect( () => {
		// besides the first render after a send attempt clear the attempt flag
		if ( intendedToSend && invoice?.id && initialStateReady ) {
			const retryPayload = JSON.parse(
				intendedToSend
			) as IntendedSendRetryPayload;
			removeSessionStorageItem( INTENDED_TO_SEND_KEY );
			// skip the additional save to avoid a loop
			if ( retryPayload.intent === SendIntent.CustomizeAndSend ) {
				const valid = validate( true );
				if ( valid ) {
					setOpenCustomizeEmailModal( true );
				}
			} else {
				performSend( undefined, true );
			}
		}
	}, [
		intendedToSend,
		performSend,
		invoice?.id,
		removeSessionStorageItem,
		validate,
		setOpenCustomizeEmailModal,
		initialStateReady,
	] );

	const { showIfRemindersDisabled, headsUpModalJsx } = useHeadsUpModal( {
		onGotIt: performSend,
	} );

	const onClick = useCallback(
		async (
			customizedEmailOrMouseEvent?:
				| CustomizedEmail
				| React.MouseEvent<HTMLElement, MouseEvent>
		) => {
			if (
				customizedEmailOrMouseEvent &&
				'intro' in customizedEmailOrMouseEvent
			) {
				return performSend( customizedEmailOrMouseEvent );
			}
			if ( showIfRemindersDisabled( invoice?.automatedRemindersEnabled ) ) {
				return false;
			}
			return performSend();
		},
		[
			invoice?.automatedRemindersEnabled,
			performSend,
			showIfRemindersDisabled
		]
	);

	const buttonGroupOptions: Pick<
		ButtonGroupButtonProps,
		'options' | 'optionsLabel' | 'buttonProps'
	> = {
		optionsLabel:
			'Save and send, or customize customer notification emails and send',
		options: [
			{
				children: buttonText,
				onClick,
			},
			{
				children: `Customize & ${ buttonText.toLowerCase() }`,
				onClick: async () => {
					if ( showIfRemindersDisabled( invoice?.automatedRemindersEnabled ) ) {
						return;
					}
					if ( !invoice?.id ) {
						// New invoice: create the draft first (loose validation) so work is
						// never lost, then let the useEffect above run strict validation on
						// the new page and open the modal only if everything passes.
						markIntentToSendForValidationRetry( {
							intent: SendIntent.CustomizeAndSend,
						} );
						const saveSuccess = await validateAndCreateOrUpdate( false );
						if ( !saveSuccess ) {
							return false;
						}
						await router.push( `/invoice/${ saveSuccess.id }?tab=edit`, {
							overrideBlockers: [ INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID ],
						} );
						return;
					}
					// Existing invoice: save + strictly validate, then open modal.
					const success = await validateAndSavePriorToSending();
					if ( !success ) return false;
					return setOpenCustomizeEmailModal( true );
				},
			},
		],
		buttonProps: { onClick },
	};

	const invoiceTotalsAsStrings = invoice
		? rpcShared.invoices.calculatePaymentTotalsToString( invoice )
		: undefined;

	return {
		button: {
			[ buttonText ]: {
				isVisible: shouldRenderSendButton,
				isIconWhenSmall: false,
				'data-cy': 'send-button',
				startIcon: <Icon type='send' />,
				/* this is a little didactic in that this document can only be the last document in a bundle
				but I'm trying to make this look a little like the other documents for "consistency". In
				reality this should always be a "split" button regardless of if a bundle is happening or not.
				Now, if it's a bundle we'll show different content within the "customize email" modal, but
				we should always give them the "split button" option here. */
				...( !bundle || isLastDocumentInBundle
					? buttonGroupOptions
					: { onClick } ),
				loading,
				disabled,
			},
		},
		jsx: (
			<>
				<MuiPayToModal
					open={ showPayToModal }
					onClose={ () => setShowPayToModal( false ) }
				/>
				{ contactsContext?.contacts &&
				contactsContext?.contacts.length > 0 &&
				contactsContext.contacts[ 0 ].firstName &&
				invoiceTotalsAsStrings?.unpaidBalance ? (
						<CustomizeCustomerNotificationModal
							{ ...( bundle
								? {
									type: MessagingType.Bundle,
									bundle,
							  }
								: {
									type:
										invoice?.status === InvoiceStatus.Draft || !invoice?.status
											? MessagingType.InvoiceSent
											: MessagingType.InvoiceResent,
									unpaidBalance: invoiceTotalsAsStrings.unpaidBalance,
							  } ) }
							open={ openCustomizeEmailModal }
							onClose={ () => setOpenCustomizeEmailModal( false ) }
							onSend={ onClick }
							firstName={ contactsContext.contacts[ 0 ].firstName }
						/>
					) : null }
				{ headsUpModalJsx }
			</>
		),
	};
};
